import {
  isTransferInFlight,
  selectTransferPopupEntries,
} from "@frontend-shared/utils";
import { memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { PATHS } from "@/constants/paths";
import { useNow } from "@/hooks/use-now";
import { useTransferPopup } from "@/hooks/use-transfer-popup";
import { useTransfers } from "@/hooks/use-transfers";

import { TRANSFER_POPUP_CLOCK_INTERVAL_MS } from "./constants";
import { TransferPopupCard } from "./TransferPopupCard";
import type { ITransferPopupProps } from "./types";

// NOTE: NSAI-7067 — what makes a transfer visible from anywhere now that
// leaving the Uploads page no longer cancels it. Before this ticket a
// connector fetch pinned the user to that page behind a "leaving will cancel
// the fetch" dialog; now the fetch keeps going, and this card goes with the
// user — which file, how far, how long, and a way to stop it or get back.
const TransferPopupComponent = ({
  variant,
}: ITransferPopupProps): JSX.Element | null => {
  const { transfers, cancelTransfer } = useTransfers();
  const { hiddenIds, hideEntries } = useTransferPopup();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const selection = useMemo(
    () => selectTransferPopupEntries({ transfers, hiddenIds }),
    [transfers, hiddenIds],
  );
  const { primary } = selection;

  const now = useNow({
    isActive: selection.inFlightCount > 0,
    intervalMs: TRANSFER_POPUP_CLOCK_INTERVAL_MS,
  });

  const handleOpenUploads = useCallback((): void => {
    navigate(PATHS.UPLOADS);
  }, [navigate]);

  // NOTE: One transfer in flight: the X stops it, as the row's X on the
  // Uploads page does. A batch: the X only puts the card away — aborting
  // several uploads from one click on a summary would be far too easy to do by
  // accident. A settled card is simply let go of; its row stays on the page.
  const handleClose = useCallback((): void => {
    if (!primary) {
      return;
    }

    if (selection.inFlightCount === 1 && isTransferInFlight(primary.status)) {
      cancelTransfer(primary);
      return;
    }

    hideEntries(
      transfers
        .filter((entry) => !hiddenIds.has(entry.id))
        .map((entry) => entry.id),
    );
  }, [
    primary,
    selection.inFlightCount,
    cancelTransfer,
    hideEntries,
    transfers,
    hiddenIds,
  ]);

  // NOTE: The Uploads page already lists every transfer with its own
  // controls; a card on top of that list would only repeat it.
  if (!primary || pathname.startsWith(PATHS.UPLOADS)) {
    return null;
  }

  return (
    <TransferPopupCard
      variant={variant}
      primary={primary}
      selection={selection}
      now={now}
      onOpenUploads={handleOpenUploads}
      onClose={handleClose}
    />
  );
};

export const TransferPopup = memo(TransferPopupComponent);
